import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ford Model T (Tin Lizzie, ~1915 Touring) — a tall, upright, spindly brass-era
// horseless carriage at true scale (1 unit = 1 metre): length ~3.40, width 1.66
// (over the fenders), height ~1.98 to the raised canvas top, wheelbase 2.54, on
// big thin spoked artillery wheels (radius ~0.42). Nose is +Z. The whole read
// is ANTIQUE: a tall narrow black touring body carried high off the ground on
// large-diameter wooden-spoke wheels with lots of air beneath, a tall vertical
// brass radiator shell flanked by two round brass acetylene headlamps on tall
// brackets, a separate louvered bonnet, an open passenger tub with a raised
// black folding canvas top on thin bows, a flat vertical windscreen at the
// cowl, sweeping black mudguards with running boards, a hand-crank stub, and a
// spare spoked wheel on the tail. Body panels carry the "paint" recolour slot,
// defaulting to the iconic Model T black ("any colour so long as it is black").
// `drive` spins the wheels and `hazards` blinks four small indicators.

const PAINT = "#1c1c1e"; // Model T black — the "paint" slot default
const FENDER = "#141416"; // sweeping black mudguards / running boards (unslotted)
const BRASS = "#b5893a"; // radiator shell, lamps, trim (brass era)
const WOOD = "#8a6a3a"; // wooden artillery spokes + felloe
const TIRE = "#1a1b1d"; // thin tyres
const GLASS = "#33485c"; // flat windscreen glass
const CANVAS = "#111113"; // folding canvas top
const INT = "#3c342b"; // interior floor / dash trim (lighter so the open tub reads)
const LEATHER = "#4e3d2a"; // worn seat leather (lighter so the open cabin reads)
const LAMPGLASS = "#f2e3ad"; // warm brass headlamp lens
const BLACK = "#0e0e10"; // deep black details (radiator core, louvres, chassis)

// Spec-sheet skeleton (metres). Big thin wheels, a long wheelbase, a narrow body.
const WHEEL_R = 0.42; // large-diameter antique wheel
const WHEEL_X = 0.72; // half track — tyres proud, outboard of the narrow body
const AXLE_F = 1.27;
const AXLE_R = -1.27; // wheelbase 2.54

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

function torus(
  major: number, tube: number, arc: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.TorusGeometry(major, tube, 8, 20, arc), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One big thin wooden-spoke artillery wheel: a thin dark tyre ring, a wooden
 *  felloe just inside it, twelve thin wooden spokes (six full-diameter bars
 *  crossing the hub), a wooden hub and a brass hub cap. Every part shares
 *  `name` so `drive` spins them as one node about the axle. The tyre is a
 *  torus (an open ring), so the spokes read THROUGH it — a solid disc would
 *  hide them. `[0, pi/2, 0]` lays the ring's axis along X (the roll axis).
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    torus(0.375, 0.045, Math.PI * 2, TIRE, [x, y, z], [0, Math.PI / 2, 0], { name }),
    torus(0.31, 0.03, Math.PI * 2, WOOD, [x, y, z], [0, Math.PI / 2, 0], { name }),
    cyl(0.07, 0.07, 0.11, 12, WOOD, [x, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  // Six thin bars through the hub = twelve radial spokes, spinning with the wheel.
  for (let k = 0; k < 6; k++) {
    parts.push(box(0.035, 0.6, 0.05, WOOD, [x, y, z], [(k * Math.PI) / 6, 0, 0], { name }));
  }
  parts.push(cyl(0.045, 0.045, 0.13, 12, BRASS, [x, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The static spare wheel mounted flat against the tail: the same artillery
 *  wheel but oriented in the XY plane (axis along Z, facing back). */
function spareWheel(position: Vec3): Part[] {
  const [x, y, z] = position;
  const parts: Part[] = [
    torus(0.375, 0.045, Math.PI * 2, TIRE, [x, y, z]),
    torus(0.31, 0.03, Math.PI * 2, WOOD, [x, y, z]),
    cyl(0.07, 0.07, 0.11, 12, WOOD, [x, y, z], [Math.PI / 2, 0, 0]),
  ];
  for (let k = 0; k < 6; k++) {
    parts.push(box(0.035, 0.6, 0.05, WOOD, [x, y, z], [0, 0, (k * Math.PI) / 6]));
  }
  parts.push(cyl(0.045, 0.045, 0.13, 12, BRASS, [x, y, z], [Math.PI / 2, 0, 0]));
  return parts;
}

/** A sweeping black mudguard: a fat half-torus crown over the wheel. The
 *  `[0, pi/2, 0]` yaw drops the 0..pi arc into the ZY plane so it sweeps
 *  front-to-back over the top of the tyre. */
function fender(side: 1 | -1, z: number): Part {
  return torus(0.46, 0.09, Math.PI, FENDER, [side * WHEEL_X, WHEEL_R, z], [0, Math.PI / 2, 0]);
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Early cars had none; these are small brass-era side lamps for consistency.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.52, 1.0, 1.52] },
  { name: "indicator-fr", pos: [0.52, 1.0, 1.52] },
  { name: "indicator-rl", pos: [-0.46, 1.14, -1.46] },
  { name: "indicator-rr", pos: [0.46, 1.14, -1.46] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear: big thin spoked wheels, solid axles, high frame ----------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.04, 0.04, 1.42, 8, BLACK, [0, 0.42, z], [0, 0, Math.PI / 2])); // solid beam axle
  }
  for (const s of S) P.push(box(0.05, 0.08, 2.7, BLACK, [s * 0.34, 0.55, 0])); // high chassis rail (air below)

  // -- Fenders + running boards (unslotted black) -----------------------------
  for (const z of [AXLE_F, AXLE_R]) for (const s of S) P.push(fender(s, z));
  for (const s of S) P.push(box(0.2, 0.05, 1.5, FENDER, [s * 0.7, 0.54, 0])); // running board between the fenders

  // -- Front: tall brass radiator + separate louvered bonnet ------------------
  P.push(box(0.5, 0.84, 0.1, BRASS, [0, 1.04, 1.53])); // tall vertical brass radiator shell
  P.push(box(0.34, 0.58, 0.06, BLACK, [0, 0.98, 1.585])); // dark honeycomb core, wide brass border around it
  P.push(cyl(0.045, 0.045, 0.14, 10, BRASS, [0, 1.52, 1.53])); // tall brass filler cap on top
  P.push(box(0.5, 0.09, 0.11, BRASS, [0, 1.42, 1.545])); // brass top tank
  P.push(paint(0.46, 0.44, 1.06, [0, 1.06, 0.98])); // louvered bonnet, radiator back to cowl
  P.push(box(0.02, 0.03, 1.02, BRASS, [0, 1.29, 0.98])); // bonnet centre hinge strip
  for (const s of S) for (const zL of [0.6, 0.72, 0.84, 0.96]) {
    P.push(box(0.01, 0.26, 0.02, BLACK, [s * 0.235, 1.03, zL])); // hood-side louvres
  }
  P.push(paint(0.54, 0.42, 0.2, [0, 1.02, 0.34])); // cowl bridging bonnet to windscreen

  // -- Brass headlamps on tall brackets, flanking the radiator ----------------
  for (const s of S) {
    P.push(cyl(0.11, 0.11, 0.17, 14, BRASS, [s * 0.53, 1.32, 1.5], [Math.PI / 2, 0, 0])); // lamp drum
    P.push(cyl(0.095, 0.095, 0.03, 14, LAMPGLASS, [s * 0.53, 1.32, 1.61], [Math.PI / 2, 0, 0])); // warm lens
    P.push(box(0.03, 0.5, 0.03, BRASS, [s * 0.53, 1.05, 1.44])); // tall mounting bracket up from the frame
  }
  P.push(box(0.94, 0.03, 0.03, BRASS, [0, 1.32, 1.45])); // brass tie-bar between the lamps

  // -- Passenger tub: solid lower body + open-top side/rear walls --------------
  P.push(paint(1.02, 0.34, 1.85, [0, 0.78, -0.55])); // lower body, cowl to tail
  for (const s of S) P.push(paint(0.06, 0.34, 1.7, [s * 0.48, 1.11, -0.53])); // door-top side walls
  P.push(paint(1.02, 0.34, 0.08, [0, 1.11, -1.42])); // rear wall
  P.push(box(0.9, 0.3, 0.06, INT, [0, 1.08, 0.33])); // dash / firewall behind the cowl

  // -- Open interior: floor, two bench seats, a steering wheel -----------------
  P.push(box(0.9, 0.02, 1.6, INT, [0, 0.955, -0.5])); // interior floor
  P.push(box(0.9, 0.12, 0.4, LEATHER, [0, 1.0, -0.05])); // front seat cushion
  P.push(box(0.9, 0.28, 0.1, LEATHER, [0, 1.12, -0.25])); // front seat back
  P.push(box(0.9, 0.12, 0.4, LEATHER, [0, 1.0, -0.95])); // rear seat cushion
  P.push(box(0.9, 0.3, 0.1, LEATHER, [0, 1.14, -1.2])); // rear seat back
  P.push(box(0.03, 0.03, 0.45, BLACK, [-0.28, 1.16, 0.12], [0.9, 0, 0])); // steering column
  P.push(torus(0.11, 0.02, Math.PI * 2, BLACK, [-0.28, 1.33, -0.06], [1.1, 0, 0])); // steering wheel

  // -- Flat vertical windscreen at the cowl -----------------------------------
  P.push(box(0.94, 0.52, 0.03, BRASS, [0, 1.48, 0.45])); // thin brass frame
  P.push(box(0.86, 0.44, 0.02, GLASS, [0, 1.48, 0.455])); // flat glass pane

  // -- Raised black folding canvas top on thin bows ----------------------------
  for (const s of S) {
    P.push(box(0.035, 0.6, 0.035, BLACK, [s * 0.46, 1.55, 0.44])); // front bow post (off windscreen top)
    P.push(box(0.04, 0.6, 0.04, BLACK, [s * 0.46, 1.55, -1.36])); // rear bow post
    P.push(box(0.04, 0.05, 1.9, CANVAS, [s * 0.48, 1.83, -0.46])); // side rail
  }
  P.push(box(0.98, 0.06, 1.96, CANVAS, [0, 1.86, -0.46])); // canvas roof over both rows
  P.push(box(0.98, 0.28, 0.05, CANVAS, [0, 1.7, -1.44])); // rear curtain closing the top

  // -- Spare spoked wheel on the tail -----------------------------------------
  P.push(box(0.05, 0.05, 0.18, BLACK, [0, 0.95, -1.53])); // spare mount bracket
  P.push(...spareWheel([0, 0.95, -1.64]));

  // -- Hand-crank stub at the base of the radiator ----------------------------
  P.push(box(0.03, 0.03, 0.16, BLACK, [0, 0.62, 1.68])); // crank shaft
  P.push(box(0.11, 0.03, 0.03, BLACK, [0.05, 0.62, 1.75])); // crank handle

  // -- Small brass-era side lamps (the hazard indicators) ---------------------
  for (const ind of INDICATORS) {
    P.push(cyl(0.035, 0.035, 0.08, 10, "#e8a13a", ind.pos, [Math.PI / 2, 0, 0], { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const fordModelT = defineModel({
  id: "ford-model-t",
  name: "Ford Model T",
  batchId: "cars",
  tags: ["car", "ford", "model-t", "classic", "antique", "veteran"],
  summary: "A tall, upright 1915 Ford Model T Touring in iconic black, on big spoked wheels.",
  description:
    "A Ford Model T (Tin Lizzie, ~1915 Touring) at real 1 unit = 1 metre scale " +
    "(about 3.40 long, 1.66 wide over the fenders, 1.98 tall to the raised top, " +
    "2.54 wheelbase). The read is a spindly brass-era horseless carriage: a " +
    "tall narrow black touring body carried high off the ground on big thin " +
    "wooden-spoke artillery wheels (radius ~0.42) with plenty of air beneath, " +
    "a tall vertical brass radiator shell flanked by two round brass headlamps " +
    "on tall brackets with a tie-bar, a separate louvered bonnet, an open " +
    "passenger tub with two bench seats and a raised black folding canvas top " +
    "on thin bows, a flat vertical windscreen at the cowl, sweeping black " +
    "mudguards with running boards, a steering wheel, a hand-crank stub and a " +
    "spare spoked wheel on the tail. Body panels carry the 'paint' recolour " +
    "slot (Model T black); `drive` spins the wheels and `hazards` blinks the " +
    "four brass side lamps.",
  author: "Fable 5",
  footprint: 1.85,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
